import React, { useState, useMemo, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';

function Tournaments({ tournaments, teams, matches, socket, user }) {
  const [newTournament, setNewTournament] = useState('');
  const [deletedTournaments, setDeletedTournaments] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [top5Players, setTop5Players] = useState([]);
  const { slug } = useParams();
  const navigate = useNavigate();

  const toSlug = (value) =>
    String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

  const getTournamentPath = (tournament) => {
    const tournamentSlug = toSlug(tournament.name) || String(tournament.id);
    return `/tournaments/${encodeURIComponent(tournamentSlug)}`;
  };

  const canEdit = user && (user.role === 'SuperAdmin' || user.role === 'Admin' || user.role === 'AdminTournament');

  const selectedTournament = useMemo(() => {
    if (!slug) return null;
    const decodedSlug = decodeURIComponent(slug);
    return tournaments.find(
      (t) => toSlug(t.name) === decodedSlug || String(t.id) === decodedSlug
    );
  }, [slug, tournaments]);
  const tournamentTeamNames = selectedTournament?.teams ? selectedTournament.teams.split(',').map((name) => name.trim()) : [];
  const tournamentTeams = teams.filter((team) => tournamentTeamNames.includes(team.name));

  const tournamentMatches = useMemo(() => {
    if (!selectedTournament) return [];
    return matches.filter((match) => String(match.tournament_id) === String(selectedTournament.id));
  }, [matches, selectedTournament]);

  const formatMatchStatus = (status) => {
    switch (status) {
      case 'scheduled':
        return 'Aikataulutettu';
      case 'live':
        return 'Käynnissä';
      case 'finished':
        return 'Päättynyt';
      default:
        return status;
    }
  };

  const standings = useMemo(() => {
    if (!selectedTournament || tournamentTeams.length === 0) return [];

    // Only use finished matches for standings
    const finishedMatches = tournamentMatches.filter(m => m.status === 'finished');

    const table = tournamentTeams.reduce((acc, team) => {
      acc[team.id] = {
        teamId: team.id,
        teamName: team.name,
        played: 0,
        wins: 0,
        draws: 0,
        losses: 0,
        goalsFor: 0,
        goalsAgainst: 0,
        goalDiff: 0,
        points: 0,
      };
      return acc;
    }, {});

    finishedMatches.forEach((match) => {
      const home = table[match.home_team_id];
      const away = table[match.away_team_id];
      if (!home || !away) return;

      const homeGoals = Number(match.home_score || 0);
      const awayGoals = Number(match.away_score || 0);
      home.played += 1;
      away.played += 1;
      home.goalsFor += homeGoals;
      home.goalsAgainst += awayGoals;
      away.goalsFor += awayGoals;
      away.goalsAgainst += homeGoals;

      if (homeGoals > awayGoals) {
        home.wins += 1;
        away.losses += 1;
        home.points += 3;
      } else if (homeGoals < awayGoals) {
        away.wins += 1;
        home.losses += 1;
        away.points += 3;
      } else {
        home.draws += 1;
        away.draws += 1;
        home.points += 1;
        away.points += 1;
      }
    });

    return Object.values(table).map((row) => ({
      ...row,
      goalDiff: row.goalsFor - row.goalsAgainst,
    })).sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      if (b.goalDiff !== a.goalDiff) return b.goalDiff - a.goalDiff;
      return b.goalsFor - a.goalsFor;
    });
  }, [selectedTournament, tournamentTeams, tournamentMatches]);

  useEffect(() => {
    if (!socket) return;

    const handleTopPlayers = (rows) => {
      setTop5Players(Array.isArray(rows) ? rows : []);
    };

    socket.on('tournamentTopPlayers', handleTopPlayers);
    return () => {
      socket.off('tournamentTopPlayers', handleTopPlayers);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket || !selectedTournament) {
      setTop5Players([]);
      return;
    }
    socket.emit('getTournamentTopPlayers', selectedTournament.id);
  }, [socket, selectedTournament, matches]);

  const addTournament = (e) => {
    e.preventDefault();
    if (newTournament.trim() === '' || !socket) return;
    socket.emit('addTournament', { name: newTournament });
    setNewTournament('');
  };

  useEffect(() => {
    if (!socket) return;
    socket.on('deletedTournaments', (tournaments) => {
      setDeletedTournaments(tournaments);
    });
    return () => {
      socket.off('deletedTournaments');
    };
  }, [socket]);

  useEffect(() => {
    if (!socket || !showDeleted || !canEdit) return;
    socket.emit('getDeletedTournaments');
  }, [socket, showDeleted, canEdit]);

  useEffect(() => {
    if (!canEdit && showDeleted) {
      setShowDeleted(false);
    }
  }, [canEdit, showDeleted]);

  const handleDeleteTournament = (tournamentId, name) => {
    if (!socket) return;
    if (window.confirm(`Poistetaanko turnaus "${name}"?`)) {
      socket.emit('deleteTournament', tournamentId);
    }
  };

  const handleRestoreTournament = (tournamentId, name) => {
    if (!socket) return;
    if (window.confirm(`Palautetaanko turnaus "${name}"?`)) {
      socket.emit('restoreTournament', tournamentId);
    }
  };

  const handleTeamTournamentChange = (tournamentId, teamId, isChecked) => {
    if (!socket) return;
    if (isChecked) {
      socket.emit('addTeamToTournament', { tournament_id: tournamentId, team_id: teamId });
    } else {
      socket.emit('removeTeamFromTournament', { tournament_id: tournamentId, team_id: teamId });
    }
  };

  if (selectedTournament) {
    return (
      <div className="tournaments-section">
        <div className="tournaments-header">
          <button type="button" onClick={() => navigate('/tournaments')}>
            ← Takaisin turnauksiin
          </button>
          <h2>{selectedTournament.name}</h2>
        </div>

        <section className="tournament-detail-block">
          <h3>Ottelut</h3>
          {tournamentMatches.length > 0 ? (
            <ul className="detail-list">
              {tournamentMatches.map((match) => (
                <li key={match.id}>
                  {match.home_team_name} {match.home_score} - {match.away_score} {match.away_team_name}
                  <span className="match-meta">{formatMatchStatus(match.status)} · {new Date(match.date).toLocaleString()}</span>
                  <button type="button" className="view-btn" onClick={() => navigate('/matches', { state: { matchId: match.id } })}>Näytä</button>
                </li>
              ))}
            </ul>
          ) : (
            <p>Tähän turnaukseen ei ole aikataulutettu otteluita.</p>
          )}
        </section>

        <section className="tournament-detail-block">
          <h3>Nykyiset sijoitukset</h3>
          {standings.length > 0 ? (
            <div className="standings-table-wrapper">
              <table className="standings-table">
                <thead>
                  <tr>
                    <th>Joukkue</th>
                    <th>P</th>
                    <th>V</th>
                    <th>T</th>
                    <th>H</th>
                    <th>TM</th>
                    <th>PM</th>
                    <th>ME</th>
                    <th>Pisteet</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((row) => (
                    <tr key={row.teamId}>
                      <td>{row.teamName}</td>
                      <td>{row.played}</td>
                      <td>{row.wins}</td>
                      <td>{row.draws}</td>
                      <td>{row.losses}</td>
                      <td>{row.goalsFor}</td>
                      <td>{row.goalsAgainst}</td>
                      <td>{row.goalDiff}</td>
                      <td>{row.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>Sarjataulukkoa ei ole vielä saatavilla.</p>
          )}
        </section>

        <section className="tournament-detail-block">
          <h3>Top 5 pelaajat</h3>
          {top5Players.length > 0 ? (
            <ol className="top-players-list">
              {top5Players.map((player) => (
                <li key={player.player_id}>
                  <strong>{player.player_name}</strong>
                  <span className="player-stats">{player.points} pistettä ({player.goals}M + {player.assists}S)</span>
                </li>
              ))}
            </ol>
          ) : (
            <p>Ei pelaajatilastoja saatavilla.</p>
          )}
        </section>

        <section className="tournament-detail-block">
          <h3>Joukkueet</h3>
          {tournamentTeams.length > 0 ? (
            <ul className="detail-list">
              {tournamentTeams.map((team) => (
                <li key={team.id}>{team.name}</li>
              ))}
            </ul>
          ) : (
            <p>Tähän turnaukseen ei ole lisätty joukkueita.</p>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className="tournaments-section">
      <h2>Turnaukset</h2>
      {canEdit && (
        <button type="button" className="toggle-button" onClick={() => setShowDeleted((prev) => !prev)}>
          {showDeleted ? 'Näytä aktiiviset turnaukset' : 'Näytä poistetut turnaukset'}
        </button>
      )}
      {showDeleted ? (
        <div>
          <h3>Poistetut turnaukset</h3>
          {deletedTournaments.length > 0 ? (
            <ul className="tournaments-list">
              {deletedTournaments.map((t) => (
                <li key={t.id} className="tournament-card">
                  <div>
                    <strong>{t.name}</strong>
                  </div>
                  {canEdit && (
                    <div className="tournament-actions">
                      <button type="button" className="restore-button" onClick={() => handleRestoreTournament(t.id, t.name)}>
                        Palauta
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p>Poistettuja turnauksia ei ole.</p>
          )}
        </div>
      ) : (
        <>
          {canEdit && (
            <form onSubmit={addTournament}>
              <input
                type="text"
                value={newTournament}
                onChange={(e) => setNewTournament(e.target.value)}
                placeholder="Anna uuden turnauksen nimi"
              />
              <button type="submit">Lisää turnaus</button>
            </form>
          )}

          <ul className="tournaments-list">
            {tournaments.map((t) => (
              <li key={t.id} className="tournament-card">
                <div>
                  <strong>{t.name}</strong>
                  <div className="team-chip-list">
                    {t.teams ? t.teams.split(',').map((teamName) => (
                      <span key={teamName} className="team-chip">{teamName.trim()}</span>
                    )) : <span className="team-chip">Ei joukkueita</span>}
                  </div>
                </div>
                <div className="tournament-actions">
                  <Link to={getTournamentPath(t)} className="view-link">Näytä</Link>
                  {canEdit && (
                    <button type="button" className="delete-button" onClick={() => handleDeleteTournament(t.id, t.name)}>
                      Poista
                    </button>
                  )}
                </div>
                {canEdit && (
                  <div className="team-selection-list">
                    {teams.map(team => (
                      <label key={team.id}>
                        <input
                          type="checkbox"
                          checked={t.teams && t.teams.split(',').includes(team.name)}
                          onChange={(e) => handleTeamTournamentChange(t.id, team.id, e.target.checked)}
                          disabled={!canEdit}
                        />
                        {team.name}
                      </label>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default Tournaments;
